import type { Injector } from '@angular/core';
import type { OnyxOption } from '@onyx/angular';
import { uniqBy } from 'lodash';
import { ValidationSeverity } from '../../../../../common/enums/validation-severity';
import { CommonHelper } from '../../../../../common/helpers/common.helper';
import { ValidationContext } from '../../../../../common/helpers/validation-conditions.helper';
import type { ValidationIssue } from '../../../../../common/interfaces/validation/validation-issue';
import { DriverValidationHelper } from '../../../../drivers/common/helpers/driver-validation.helper';
import type { Driver } from '../../../../drivers/common/interfaces/driver';
import { FleetCategory } from '../../../../fleet/common/enums/fleet-category';
import { FleetValidationHelper } from '../../../../fleet/common/helpers/fleet-validation.helper';
import type { Fleet } from '../../../../fleet/common/interfaces/fleet';
import type { SwapSetError } from '../../enums/swap/swap-error';
import { SwapSetAction } from '../../enums/swap/swap-set-action';
import { SwapVehicleType } from '../../enums/swap/swap-vehicle-type';
import type { SwapRoutePoint } from '../../interfaces/swap/swap-route-point';
import type { SwapSet } from '../../interfaces/swap/swap-set';
import type { SwapVehicleData } from '../../stores/swap.store';
import { SwapHelper } from './swap.helper';

export class SwapDetailsHelper {
  public static INACTIVE_ORDER_ACTIONS = [
    SwapSetAction.UNLOAD_IN_POINT,
    SwapSetAction.DROP_OFF_IN_POINT,
    SwapSetAction.DROP_OFF_ON_SEMI_TRAILER,
  ];

  public static getActionLabelKey(
    action: SwapSetAction,
    type: SwapVehicleType,
    context: 'current' | 'past',
  ): string {
    const I18N = 'map.swapPanel.actions';
    const PAST_LABEL_KEYS: Partial<Record<SwapSetAction, string>> = {
      [SwapSetAction.UNLOAD_IN_POINT]: 'unloadedInPoint',
      [SwapSetAction.DROP_OFF_IN_POINT]: 'droppedOffInPoint',
      [SwapSetAction.DROP_OFF_ON_SEMI_TRAILER]: 'droppedOffOnSemiTrailer',
    };

    const isPrimary = type === SwapVehicleType.PRIMARY;

    if (action === SwapSetAction.TRANSFER) {
      if (context === 'current') {
        return `${I18N}.${isPrimary ? 'transferToSecondarySet' : 'transferToPrimarySet'}`;
      }
      return `${I18N}.${isPrimary ? 'transferredFromSecondarySet' : 'transferredFromPrimarySet'}`;
    }

    if (action === SwapSetAction.SWAP) {
      if (context === 'current') {
        return `${I18N}.${isPrimary ? 'swapToSecondarySet' : 'swapToPrimarySet'}`;
      }
      return `${I18N}.${isPrimary ? 'swappedFromSecondarySet' : 'swappedFromPrimarySet'}`;
    }

    if (context === 'past') {
      const key = PAST_LABEL_KEYS[action];
      if (key) return `${I18N}.${key}`;
    }

    return `${I18N}.${action}`;
  }

  public static getDriverOptions(
    action: SwapSetAction,
    isDouble: boolean,
    type: SwapVehicleType,
    isManuallyAdded?: boolean,
  ): OnyxOption<SwapSetAction>[] {
    const getOption = (value: SwapSetAction) =>
      SwapDetailsHelper.getOption(value, type, action);

    return [
      getOption(SwapSetAction.NONE),
      ...(isManuallyAdded
        ? [getOption(SwapSetAction.DELETE)]
        : [
            ...(action !== SwapSetAction.TRANSFER
              ? [getOption(SwapSetAction.DROP_OFF_IN_POINT)]
              : []),
            ...(isDouble ? [getOption(SwapSetAction.TRANSFER)] : []),
          ]),
    ];
  }

  public static getTrailerOptions(
    action: SwapSetAction,
    trailer: Fleet,
    isDouble: boolean,
    data: SwapVehicleData,
    otherData: SwapVehicleData,
    otherSetHasCargoSpace: boolean,
    type: SwapVehicleType,
    isManuallyAdded?: boolean,
  ): OnyxOption<SwapSetAction>[] {
    const getOption = (value: SwapSetAction) =>
      SwapDetailsHelper.getOption(value, type, action);

    const canDropOff =
      ![SwapSetAction.TRANSFER, SwapSetAction.SWAP].includes(action) &&
      trailer.generalInformation.category === FleetCategory.SEMI_TRAILER;

    const canTransfer =
      isDouble &&
      action !== SwapSetAction.DROP_OFF_IN_POINT &&
      SwapHelper.isTrailerCompatible(otherData.vehicle, trailer) &&
      (action === SwapSetAction.TRANSFER ||
        (!otherSetHasCargoSpace && !otherData.set.trailer?.isManuallyAdded));
    SwapHelper.isTrailerCompatible(otherData.vehicle, trailer);

    const canSwap =
      isDouble &&
      action !== SwapSetAction.DROP_OFF_IN_POINT &&
      otherData.set.trailer.action !== SwapSetAction.DROP_OFF_IN_POINT &&
      !otherData.set.trailer.isManuallyAdded &&
      (action === SwapSetAction.SWAP ||
        SwapHelper.canSwapTrailers(
          data.vehicle,
          data.set.trailer.value,
          otherData.vehicle,
          otherData.set.trailer.value,
        ));

    return [
      ...(!(
        action === SwapSetAction.TRANSFER &&
        otherData.set.trailer?.isManuallyAdded
      )
        ? [getOption(SwapSetAction.NONE)]
        : []),
      ...(isManuallyAdded
        ? [getOption(SwapSetAction.DELETE)]
        : [
            ...(canDropOff ? [getOption(SwapSetAction.DROP_OFF_IN_POINT)] : []),
            ...(canTransfer ? [getOption(SwapSetAction.TRANSFER)] : []),
            ...(canSwap ? [getOption(SwapSetAction.SWAP)] : []),
          ]),
    ];
  }

  public static getOrderOptions(data: {
    action: SwapSetAction;
    isDouble: boolean;
    currentSetHasCargoSpace: boolean;
    otherSetHasCargoSpace: boolean;
    trailerAction: SwapSetAction;
    type: SwapVehicleType;
  }): OnyxOption<SwapSetAction>[] {
    const {
      action,
      isDouble,
      currentSetHasCargoSpace,
      otherSetHasCargoSpace,
      trailerAction,
      type,
    } = data;
    const getOption = (value: SwapSetAction) =>
      SwapDetailsHelper.getOption(value, type, action);
    const canTransferOrder = isDouble && otherSetHasCargoSpace;

    if (action === SwapSetAction.TRANSFER) {
      return [
        ...(canTransferOrder ? [getOption(SwapSetAction.NONE)] : []),
        getOption(SwapSetAction.UNLOAD_IN_POINT),
        getOption(SwapSetAction.TRANSFER),
      ];
    }

    if (action === SwapSetAction.DROP_OFF_ON_SEMI_TRAILER) {
      return [
        getOption(SwapSetAction.DROP_OFF_ON_SEMI_TRAILER),
        getOption(SwapSetAction.UNLOAD_IN_POINT),
        ...(canTransferOrder ? [getOption(SwapSetAction.TRANSFER)] : []),
      ];
    }

    if (action === SwapSetAction.DROP_OFF_IN_POINT) {
      return [
        getOption(SwapSetAction.NONE),
        getOption(SwapSetAction.DROP_OFF_IN_POINT),
        ...(canTransferOrder ? [getOption(SwapSetAction.TRANSFER)] : []),
      ];
    }

    if (action === SwapSetAction.UNLOAD_IN_POINT) {
      return [
        ...(currentSetHasCargoSpace ? [getOption(SwapSetAction.NONE)] : []),
        getOption(SwapSetAction.UNLOAD_IN_POINT),
        ...(trailerAction === SwapSetAction.DROP_OFF_IN_POINT
          ? [getOption(SwapSetAction.DROP_OFF_ON_SEMI_TRAILER)]
          : []),
        ...(canTransferOrder ? [getOption(SwapSetAction.TRANSFER)] : []),
      ];
    }

    return [
      getOption(SwapSetAction.NONE),
      getOption(SwapSetAction.UNLOAD_IN_POINT),
      ...(canTransferOrder ? [getOption(SwapSetAction.TRANSFER)] : []),
    ];
  }

  public static getDriverIssues(
    driver: Driver,
    set: SwapSet,
    injector: Injector,
    swapErrors?: SwapSetError[],
  ): ValidationIssue[] {
    const driverValidationHelper = injector.get(DriverValidationHelper);

    const { value, action } =
      set.drivers.find(({ value }) => value?.uuid === driver.uuid) ?? {};
    if (!value || action === SwapSetAction.DROP_OFF_IN_POINT) return [];

    const drivers = set.drivers
      .filter(
        ({ action, value }) =>
          !!value && action !== SwapSetAction.DROP_OFF_IN_POINT,
      )
      .map(({ value }) => value!);

    const vehicle = set.vehicle.value ?? undefined;
    const trailer = set.trailer.value ?? undefined;

    const issues = driverValidationHelper.validate({
      context: ValidationContext.SWAP_PANEL,
      driver: value,
      options: {
        vehicle,
        trailer:
          set.trailer.action !== SwapSetAction.DROP_OFF_IN_POINT
            ? trailer
            : undefined,
        activeOrders: set.activeOrders,
        drivers,
      },
    }).issues;

    const errors: ValidationIssue[] =
      swapErrors?.map((error) => ({
        message: `map.swapPanel.errors.${error}`,
        severity: ValidationSeverity.ERROR,
      })) ?? [];

    return uniqBy([...errors, ...issues], ({ message }) => message);
  }

  public static getTrailerIssues(
    trailer: Fleet,
    set: SwapSet,
    injector: Injector,
    swapErrors?: SwapSetError[],
  ): ValidationIssue[] {
    const fleetValidationHelper = injector.get(FleetValidationHelper);

    const { action } = set.trailer;
    const vehicle = set.vehicle.value;
    if (action === SwapSetAction.DROP_OFF_IN_POINT || !vehicle) return [];

    const drivers = set.drivers
      .filter(
        ({ action, value }) =>
          !!value && action !== SwapSetAction.DROP_OFF_IN_POINT,
      )
      .map(({ value }) => value!);

    const issues = fleetValidationHelper.validateSets({
      context: ValidationContext.SWAP_PANEL,
      primary: vehicle,
      options: {
        warningsFocus: 'trailer',
        secondary: trailer,
        driver: drivers[0],
        secondaryDriver: drivers[1],
        activeOrders: set.activeOrders,
      },
    }).issues;

    const errors: ValidationIssue[] =
      swapErrors?.map((error) => ({
        message: `map.swapPanel.errors.${error}`,
        severity: ValidationSeverity.ERROR,
      })) ?? [];

    return uniqBy([...errors, ...issues], ({ message }) => message);
  }

  public static getOrderIssues(set: SwapSet): ValidationIssue[] {
    const { activeOrders, drivers } = set;

    const hasActiveOrders = activeOrders.some(
      ({ value, action }) =>
        !!value && !SwapDetailsHelper.INACTIVE_ORDER_ACTIONS.includes(action),
    );
    if (!hasActiveOrders) return [];

    const hasActiveDrivers = drivers.some(
      ({ value, action }) =>
        !!value && action !== SwapSetAction.DROP_OFF_IN_POINT,
    );
    if (hasActiveDrivers) return [];

    return [
      {
        severity: ValidationSeverity.WARNING,
        message: 'map.swapPanel.orderWithoutDriver',
      },
    ];
  }

  public static getPointCycleIndexes(
    route: SwapRoutePoint[],
    originalRoute: SwapRoutePoint[],
    transferredOrderUuids: Set<string>,
  ): number[] {
    const isTransferred = (item: SwapRoutePoint): boolean => {
      const orderUuid = SwapHelper.getPointOrderUuid(item);
      return transferredOrderUuids.has(orderUuid ?? '');
    };

    const minimumCycleIndex = route.reduce((minimumCycleIndex, item) => {
      if (item.isSwapPoint || isTransferred(item)) return minimumCycleIndex;

      const { cycleIndex } = item.point;
      if (cycleIndex < 0) return minimumCycleIndex;

      return minimumCycleIndex === -1
        ? cycleIndex
        : Math.min(minimumCycleIndex, cycleIndex);
    }, -1);

    const cycleIndexByUuid = new Map<string, number>();
    let currentCycleIndex = -1;

    for (const item of originalRoute) {
      const uuid = SwapHelper.getPointUuid(item);

      if (item.isSwapPoint) {
        cycleIndexByUuid.set(uuid, -1);
        continue;
      }

      if (isTransferred(item)) {
        currentCycleIndex =
          currentCycleIndex < 0
            ? Math.max(minimumCycleIndex, 0)
            : currentCycleIndex + 1;
      } else {
        const cycleIndex = item.point.cycleIndex;
        const hasOverlap =
          currentCycleIndex >= 0 && cycleIndex <= currentCycleIndex;

        currentCycleIndex = hasOverlap ? currentCycleIndex + 1 : cycleIndex;
      }

      cycleIndexByUuid.set(uuid, currentCycleIndex);
    }

    return route.map(
      (item) => cycleIndexByUuid.get(SwapHelper.getPointUuid(item)) ?? -1,
    );
  }

  public static getPointLetter(cycleIndex: number): string | null {
    if (cycleIndex < 0) return null;
    return CommonHelper.getAlphabetLetter(cycleIndex);
  }

  public static getIsDedicatedPointClass(
    points: SwapRoutePoint[],
    item: SwapRoutePoint,
    index: number,
  ): string {
    if (item.isSwapPoint || !item.point.order?.isDedicated) return '';

    const previousPoint = points
      .slice(0, index)
      .reverse()
      .find((point) => !point.isSwapPoint);
    const nextPoint = points
      .slice(index + 1)
      .find((point) => !point.isSwapPoint);

    const orderUuid = item.point.order.uuid;
    const isFirst =
      !previousPoint || previousPoint.point.order?.uuid !== orderUuid;
    const isLast = !nextPoint || nextPoint.point.order?.uuid !== orderUuid;

    if (isFirst && isLast) return 'ftl-single';
    if (isFirst) return 'ftl-first';

    const isAfterSwap = index > 0 && points[index - 1]?.isSwapPoint;
    if (isLast) return isAfterSwap ? 'ftl-last ftl-after-swap' : 'ftl-last';

    return isAfterSwap ? 'ftl-after-swap' : 'ftl-middle';
  }

  private static getOption(
    value: SwapSetAction,
    type: SwapVehicleType,
    currentAction: SwapSetAction,
  ): OnyxOption<SwapSetAction> {
    const context =
      value === currentAction && value !== SwapSetAction.NONE
        ? 'past'
        : 'current';

    return {
      name: SwapDetailsHelper.getActionLabelKey(value, type, context),
      value,
    };
  }
}
